/**
 * TEMPLATE-ISSUES-LIST.WIDGET
 * Errors then warnings of a questionnaire, as in the checks of a project :
 * one block per severity, the problems of a question together, a click opens the right place,
 * a warning can be ignored one by one
 */

import { useTranslation } from "react-i18next"
import { faArrowRight, faChevronDown, faChevronRight, faExclamationTriangle, faTimes } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ListItem from "@/components/list-item"
import ListButton from "@/components/list-button"
import Space from "@/components/space"
import Tooltip from "@/components/tooltip"
import renderBold from "@/utils/render-bold.utils"
import { useState } from "react"
import { DisplayIssue, groupIssues, groupIssuesByKind, ISSUES_GROUP_THRESHOLD, IssueGroup } from "@/utils/issues-display.utils"

interface OwnProps {
  issues: DisplayIssue[]
  onSelect?: Function //(issueId) : go to the place to fix the problem
  onDismiss?: Function //(issueId) : ignore a warning
  severity: "error" | "warning" //Severity of the issues displayed (a warning can be ignored)
  hideTitles?: boolean //No "N errors" title (the container already has one)
  groupByKind?: boolean //Many problems : one folded line per type of problem (the project), the list as in the editor otherwise
}

export function TemplateIssueLines(props: OwnProps) {
  const { t } = useTranslation()

  //Types of problem unfolded when the problems are grouped
  const [openKinds, setOpenKinds] = useState<{ [kind: string]: boolean }>({})

  const dot = (level: 1 | 2 | 3) =>
    level === 1 ? (
      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
    ) : level === 2 ? (
      <span
        className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ border: "1.5px solid currentColor", boxSizing: "border-box" }}
      />
    ) : (
      <span className="mt-2.5 h-1 w-1 shrink-0 bg-current" />
    )

  const link = () =>
    "flex items-start gap-2" +
    (props.onSelect ? " cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid" : "")

  //Same hierarchy everywhere : the question (or the rule), what is wrong under it, the details under that
  function renderLine(issue: IssueGroup, severity: "error" | "warning") {
    const canDismiss = severity === "warning" && props.onDismiss
    const dismiss = (id: string) => (
      <span className="-mr-2 ml-2 shrink-0">
        <ListButton icon={faTimes} isSmall text={t("utils_ignore")} tooltipPosition="left" onClick={() => props.onDismiss?.(id)} />
      </span>
    )
    const single = issue.items.length === 1

    const row = (level: 1 | 2 | 3, id: string, content: any, withDismiss: boolean = false) => (
      <div className="flex items-start">
        <div className={link()} onClick={() => props.onSelect?.(id)}>
          {dot(level)}
          <span>{content}</span>
        </div>
        <Space />
        {withDismiss && canDismiss && dismiss(id)}
      </div>
    )

    //A message without detail : one line
    if (single && !issue.items[0].text && !(issue.items[0].details || []).length) {
      return <div key={issue.key}>{row(1, issue.items[0].id, renderBold(issue.title), true)}</div>
    }

    return (
      <div key={issue.key}>
        {row(1, issue.items[0].id, <b>{issue.title} :</b>, single)}
        <div className="mt-1 flex flex-col gap-1 pl-3.5">
          {issue.items.map((item) => {
            const hasDetails = !!item.details && item.details.length > 0

            //Details without introduction : they are the lines under the title
            if (hasDetails && !item.intro) {
              return item.details!.map((detail, i) => (
                <div key={item.id + i}>{row(2, item.id, renderBold(detail))}</div>
              ))
            }

            return (
              <div key={item.id}>
                {row(2, item.id, renderBold(hasDetails ? item.intro! : item.text), !single)}
                {hasDetails && (
                  <div className="mt-1 flex flex-col gap-1 pl-3.5">
                    {item.details!.map((detail, i) => (
                      <div key={i}>{row(3, item.id, renderBold(detail))}</div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  //A lot of problems : one line per type with its number, folded, and a link to the first one
  if (props.groupByKind && props.issues.length > ISSUES_GROUP_THRESHOLD) {
    return (
      <div className="flex flex-col gap-1">
        {groupIssuesByKind(props.issues).map((group) => {
          const isOpen = !!openKinds[group.kind]
          return (
            <div key={group.kind}>
              <div
                className="flex cursor-pointer items-start gap-2"
                onClick={() => setOpenKinds({ ...openKinds, [group.kind]: !isOpen })}>
                <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="mt-1 w-3 shrink-0" />
                <b>
                  {t("template_issue_group_" + group.kind)} ({group.issues.length})
                </b>
                <Space />
                {props.onSelect && (
                  <Tooltip text={t("template_issue_group_first")} position="left" usePortal>
                    <button
                      type="button"
                      className="block shrink-0 cursor-pointer border-0 bg-transparent px-1 text-inherit hover:opacity-70"
                      aria-label={t("template_issue_group_first")}
                      onClick={(e) => {
                        e.stopPropagation()
                        props.onSelect?.(group.issues[0].id)
                      }}>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </Tooltip>
                )}
              </div>
              {isOpen && (
                <div className="mt-1 pl-5">
                  <TemplateIssueLines {...props} groupByKind={false} issues={group.issues} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return <div className="flex flex-col gap-1">{groupIssues(props.issues).map((group) => renderLine(group, props.severity))}</div>
}

export default function TemplateIssuesList(props: Omit<OwnProps, "severity">) {
  const { t } = useTranslation()

  function renderBlock(severity: "error" | "warning") {
    const list = props.issues.filter((x) => x.severity === severity)
    if (list.length === 0) return null
    return (
      <div
        className={"setup-card-alert " + (severity === "error" ? "setup-card-alert-error" : "setup-card-alert-warning")}
        style={{ marginTop: 0 }}>
        <ListItem isWithoutBorder>
          <div className="w-full">
            {!props.hideTitles && (
              <div className="flex items-center gap-2 text-base font-bold">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {t(
                  (severity === "error" ? "template_issues_count" : "template_warnings_count") +
                    (list.length > 1 ? "_multiple" : ""),
                  { count: list.length }
                )}
              </div>
            )}
            <div className={(props.hideTitles ? "" : "mt-2 ") + "flex flex-col gap-1 text-sm"}>
              <TemplateIssueLines {...props} issues={list} severity={severity} />
            </div>
          </div>
        </ListItem>
      </div>
    )
  }

  return (
    <>
      {renderBlock("error")}
      {renderBlock("warning")}
    </>
  )
}
